import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import Head from "next/head";
import Link from "next/link";
import { useEffect, useState } from "react";
import { IconChevronRight } from "../../../styles/icons";
import ArticleSidebar from "../../../src/articles/ArticleSidebar";
import ArticlePreviewList from "../../../src/articles/ArticlePreviewList";
import LoadingSpinner from "../../../src/components/common/LoadingSpinner";
import StoryblokClient from "storyblok-js-client";
import dynamic from "next/dynamic";

const ArticleSearchInput = dynamic(
  () => import("../../../src/articles/ArticleSearchInput")
);

const Storyblok = new StoryblokClient({
  accessToken: process.env.NEXT_PUBLIC_STORYBLOK_KEY,
  region: "eu",
});

export default function Latest() {
  const [sortBy, setSortBy] = useState("Most Recent");
  const title = `Explore our latest articles`;
  const description = `Explore our latest articles. We have posts from writers, therapists, and people like you. Contact us if want to share your experience of therapy.`;

  const [articles, setArticles] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const searchArticles = async () => {
      setLoading(true);
      let articlesData = {};
      if (!articles[currentPage]) {
        const { data } = await Storyblok.get(`cdn/stories`, {
          starts_with: "article",
          sort_by: "content.legacy_published_at:desc",
          resolve_relations: ["article.authors"],
          page: currentPage,
          per_page: 10,
        });
        articlesData[currentPage] = data.stories;
      }
      if (!articles[currentPage + 1]) {
        const { data } = await Storyblok.get(`cdn/stories`, {
          starts_with: "article",
          sort_by: "content.legacy_published_at:desc",
          resolve_relations: ["article.authors"],
          page: currentPage + 1,
          per_page: 10,
        });
        articlesData[currentPage + 1] = data.stories;
      }
      setArticles((current) => ({
        ...current,
        ...articlesData,
      }));
      setLoading(false);
    };
    searchArticles();
  }, [currentPage]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="description" content={description} />
        <meta name="og:description" content={description} />
        <meta name="keywords" content={"Latest Articles"} />
        <link rel="canonical" href={`https://welldoing.org/article/latest`} />
      </Head>
      <Container
        maxW={{ base: "100%", xl: "85%" }}
        boxSizing="border-box"
        overflowX="hidden"
      >
        <Box width="100%" display="flex">
          <Box mx="auto" my="50px" width="100%">
            <Flex align="center" gap="10px">
              <Link href="/" legacyBehavior>
                <Text color="brand.400" cursor="pointer">{`Homepage`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Link href="/article" legacyBehavior>
                <Text
                  color="brand.400"
                  cursor="pointer"
                >{`Welldoing Articles`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Text>Latest</Text>
            </Flex>
            <Box my="50px">
              <Flex columnGap="120px">
                <Flex flex={2} direction="column">
                  <Flex
                    justify="space-between"
                    direction={{ base: "column", xl: "row" }}
                  >
                    <Flex direction="column" w={{ base: "100%", xl: "auto" }}>
                      <Text as="h1" textStyle="h1" mb="15px">
                        Latest
                      </Text>
                      <Text textStyle="body1" color="brand.200">
                        {`Explore our latest articles`}
                      </Text>
                      <Box display={{ base: "block", xl: "none" }} mt="20px">
                        <ArticleSearchInput />
                      </Box>
                    </Flex>
                  </Flex>
                  <Divider borderWidth="1px" mt="40px" />
                  {loading ? (
                    <>
                      <LoadingSpinner />
                    </>
                  ) : (
                    <>
                      {articles[currentPage].map((article) => {
                        const author = article.content.authors[0];
                        const authorName = author
                          ? `${author.content.firstName} ${author.content.lastName}`
                          : "Unknown";
                        return (
                          <ArticlePreviewList
                            key={article.id}
                            storyblokImage={article.content.headerImage}
                            title={article.content.title}
                            author={authorName}
                            slug={article.slug}
                            date={
                              article.content.legacy_published_at !== ""
                                ? article.content.legacy_published_at
                                : article.first_published_at
                            }
                          />
                        );
                      })}
                    </>
                  )}
                  <Flex direction="row">
                    {currentPage > 1 && (
                      <Button
                        w="145px"
                        mt="40px"
                        onClick={() => {
                          setCurrentPage((current) => current - 1);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        Previous Page
                      </Button>
                    )}
                    {articles[currentPage + 1]?.length > 0 && (
                      <Button
                        w="145px"
                        mt="40px"
                        onClick={() => {
                          setCurrentPage((current) => current + 1);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        Next Page
                      </Button>
                    )}
                  </Flex>
                </Flex>
                <ArticleSidebar />
              </Flex>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
}
